import { MdOutlinePix } from 'react-icons/md';

import { BRAND_PROPERTIES } from 'commons';
import { integerToCurrency } from 'helpers';
import { Brand, ConsignmentDetails, ReturnDetails } from 'types';

interface ProofOfReturnProps {
  brand: Brand;
  consignmentDetails: ConsignmentDetails;
  returnDetails: ReturnDetails;
}

const PIX_PAYMENT_TYPE = 'Pix-MsPayment';

export function ProofOfReturn({
  brand,
  consignmentDetails,
  returnDetails,
}: ProofOfReturnProps) {
  const { createdDate, customer, payment } = consignmentDetails;
  const { refundedDate, refundPrice } = returnDetails;
  const { firstName, lastName, cpf } = customer;
  const { totalPrice, transactionId, paymentInfo, paymentType } = payment;
  const { accountHolderName, cardType } = paymentInfo;

  const isPixPayment = paymentType === PIX_PAYMENT_TYPE;
  const price = integerToCurrency(totalPrice.value);
  const refund = integerToCurrency(refundPrice.value);

  function renderPayment() {
    if (isPixPayment) {
      return (
        <div
          className="flex items-center gap-2 p-2 bg-neutral-200 print:bg-neutral-200
            print:[-webkit-print-color-adjust:exact] print:[print-color-adjust:exact]"
        >
          <MdOutlinePix className="text-neutral-500" />
          <p className="flex-grow text-font12 font-bold text-neutral-500">
            Reembolso realizado para a conta utilizada no pagamento Pix.
          </p>
        </div>
      );
    }

    return (
      <>
        <div
          className="flex items-center gap-2 p-2 bg-neutral-200 print:bg-neutral-200
            print:[-webkit-print-color-adjust:exact] print:[print-color-adjust:exact]"
        >
          <p className="text-font12 font-bold text-neutral-500">
            Dados do titular
          </p>
        </div>
        <div className="flex flex-col gap-3 px-2">
          <div className="flex items-center gap-12">
            <p className="min-w-40 text-font12 text-neutral-700">
              Nome do titular
            </p>
            <p className="text-font12 text-neutral-700">{accountHolderName}</p>
          </div>
          <div className="flex items-center gap-12">
            <p className="min-w-40 text-font12 text-neutral-700">CPF</p>
            <p className="text-font12 text-neutral-700">{cpf}</p>
          </div>
          <div className="flex items-center gap-12">
            <p className="min-w-40 text-font12 text-neutral-700">Banco</p>
            <p className="text-font12 text-neutral-700">{cardType}</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <div
      className="flex flex-col max-w-full gap-12 p-6 bg-neutral-100 font-printable print:bg-white
        print:p-0"
    >
      <div
        className="flex justify-between items-center px-8 pt-10 pb-4 bg-neutral-500
          text-neutral-100 print:text-neutral-100 print:bg-neutral-500
          print:[-webkit-print-color-adjust:exact] print:[print-color-adjust:exact]"
      >
        <p className="text-font14 font-bold">Comprovante de cancelamento</p>
        <div
          className="[&>svg]:w-auto [&>svg]:h-[18px] [&>svg]:fill-white
            print:[&>svg]:fill-neutral-100 print:[&>svg]:[-webkit-print-color-adjust:exact]"
        >
          {BRAND_PROPERTIES[brand].icon}
        </div>
      </div>
      <div className="flex flex-col gap-8 px-8">
        <p className="text-font12 text-neutral-700">
          <span className="font-bold">
            Olá, {firstName} {lastName}
          </span>
          <br />
          <br />
          <span className="font-bold">
            Sua compra foi cancelada com sucesso
          </span>
          <span> e o </span>
          <span className="font-bold">valor já foi devolvido para a conta</span>
          <span> de origem do pagamento.</span>
        </p>
        <div className="flex flex-col gap-6">
          <div
            className="flex items-center gap-2 p-2 bg-neutral-200 print:bg-neutral-200
              print:[-webkit-print-color-adjust:exact] print:[print-color-adjust:exact]"
          >
            <p className="text-font12 font-bold text-neutral-500">
              Dados do estorno
            </p>
          </div>
          <div className="flex flex-col gap-3 px-2">
            <div className="flex items-center gap-12">
              <p className="min-w-40 text-font12 text-neutral-700">
                Método de pagamento
              </p>
              <p className="text-font12 text-neutral-700">
                {isPixPayment ? 'Pix' : 'Cartão'}
              </p>
            </div>
            <div className="flex items-center gap-12">
              <p className="min-w-40 text-font12 text-neutral-700">
                ID da transação
              </p>
              <p className="text-font12 text-neutral-700">{transactionId}</p>
            </div>
            <div className="flex items-center gap-12">
              <p className="min-w-40 text-font12 text-neutral-700">
                Data da compra
              </p>
              <p className="text-font12 text-neutral-700">
                {createdDate.toLocaleDateString('pt-br')}
              </p>
            </div>
            <div className="flex items-center gap-12">
              <p className="min-w-40 text-font12 text-neutral-700">
                Valor da compra
              </p>
              <p className="text-font12 text-neutral-700">{price}</p>
            </div>
            <div className="flex items-center gap-12">
              <p className="min-w-40 text-font12 text-neutral-700">
                Data do cancelamento
              </p>
              <p className="text-font12 text-neutral-700">
                {refundedDate.toLocaleDateString('pt-br')}
              </p>
            </div>
            <div className="flex items-center gap-12">
              <p className="min-w-40 text-font12 text-neutral-700">
                Valor do cancelamento
              </p>
              <p className="text-font12 text-neutral-700">{refund}</p>
            </div>
          </div>

          {renderPayment()}
        </div>
        <div className="flex flex-col gap-14">
          <p className="text-font12 font-bold text-neutral-700">
            Em caso de dúvidas, por favor entre em contato com a nossa equipe de
            relacionamento com cliente.
          </p>
          <p className="text-font12 text-neutral-700">
            Atenciosamente,
            <br />
            Equipe {BRAND_PROPERTIES[brand].displayName}
          </p>
        </div>
      </div>
    </div>
  );
}
